import { create } from 'zustand';
import api from '@/utils/api';
import { PlannerPlan } from '@/lib/types';
import { unwrap } from '@/pages/planner/api';
import { PlanDraft } from '@/pages/planner/draft';

interface LoadedRange {
  workspaceId: string;
  from: string;
  to: string;
}

interface PlannerPlansStore {
  items: PlannerPlan[];
  loadedRange: LoadedRange | null;
  error: string | null;
  isLoading: boolean;
  loadRange: (workspaceId: string, from: string, to: string) => Promise<void>;
  fetchByDate: (workspaceId: string, date: string) => Promise<PlannerPlan[]>;
  savePlan: (draft: PlanDraft) => Promise<PlannerPlan>;
  deletePlan: (id: number) => Promise<void>;
  plansByDate: () => Record<string, PlannerPlan[]>;
}

// A date now holds up to two plans — the workspace plan and the caller's own
// "My Plan" (other users' personal plans never reach the client). These
// helpers pick one scope out of a day's list.
export function workspacePlanOf(plans: PlannerPlan[] | undefined): PlannerPlan | undefined {
  return plans?.find((plan) => plan.scope === 'workspace');
}

export function personalPlanOf(plans: PlannerPlan[] | undefined): PlannerPlan | undefined {
  return plans?.find((plan) => plan.scope === 'personal');
}

const usePlannerPlansStore = create<PlannerPlansStore>()((set, get) => ({
  items: [],
  loadedRange: null,
  error: null,
  isLoading: false,
  loadRange: async (workspaceId, from, to) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get(
        `practice-plans/workspace/${workspaceId}/range/${from}/${to}`,
      );
      const plans = unwrap<PlannerPlan[]>(response) ?? [];
      set({ items: plans, loadedRange: { workspaceId, from, to }, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to load practice plans',
        isLoading: false,
      });
    }
  },
  fetchByDate: async (workspaceId, date) => {
    try {
      const response = await api.get(`practice-plans/workspace/${workspaceId}/date/${date}`);
      const plans = unwrap<PlannerPlan[]>(response) ?? [];
      // Merge into the cache (replacing whatever was held for this date) so
      // consumers outside the loaded calendar range — deep links, the scope
      // tab strip — read from the same reactive source as everything else.
      set((state) => ({
        items: [
          ...state.items.filter((item) => item.plan_date.substring(0, 10) !== date),
          ...plans,
        ],
      }));
      return plans;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to load practice plans' });
      throw error;
    }
  },
  savePlan: async (draft) => {
    try {
      set({ error: null });
      const response = draft.id
        ? await api.put(`practice-plans/${draft.id}`, draft)
        : await api.post('practice-plans', draft);
      const saved = unwrap<PlannerPlan>(response);
      set((state) => {
        const exists = state.items.some((item) => item.id === saved.id);
        return {
          items: exists
            ? state.items.map((item) => (item.id === saved.id ? saved : item))
            : [...state.items, saved],
        };
      });
      return saved;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to save practice plan' });
      throw error;
    }
  },
  deletePlan: async (id) => {
    try {
      set({ error: null });
      await api.delete(`practice-plans/${id}`);
      set((state) => ({ items: state.items.filter((item) => item.id !== id) }));
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to delete practice plan' });
      throw error;
    }
  },
  plansByDate: () => {
    const map: Record<string, PlannerPlan[]> = {};
    for (const plan of get().items) {
      (map[plan.plan_date.substring(0, 10)] ??= []).push(plan);
    }
    // Workspace plan first — the tab strip and calendar dots render in this
    // order.
    for (const date of Object.keys(map)) {
      map[date].sort((a, b) => (a.scope === b.scope ? 0 : a.scope === 'workspace' ? -1 : 1));
    }
    return map;
  },
}));

export default usePlannerPlansStore;
